/**
 * Work out why a request failed.
 *
 * Auth is checked first: a 401/403 always carries a response, so it can never
 * collide with the timeout/offline/network cases below.
 *
 * @param {Object} error - The rejected axios error
 * @returns {string|number} - 'auth' | 'timeout' | 'offline' | 'network' |
 *   'unavailable' | the HTTP status code | 'unknown'
 */
export const classifyError = (error) => {
  const status = error.response?.status;

  // The service interceptor already warned the user and is redirecting to login.
  if (status === 401 || status === 403) return 'auth';

  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) return 'timeout';
  if (!navigator.onLine) return 'offline';
  if (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ECONNREFUSED' ||
    error.message?.includes('Network Error') ||
    !error.response
  ) {
    return 'network';
  }

  if (status === 502 || status === 503 || status === 504) return 'unavailable';

  return status ?? 'unknown';
};

// Wording that is the same on every screen. Anything screen-specific (and any
// case that prefers the message returned by the API) is passed as an override.
const DEFAULT_MESSAGES = {
  timeout: 'The request timed out. Please try again.',
  offline: 'No internet connection. Please check your network and try again.',
  network: 'The server is currently unavailable. Please try again later or contact support.',
  unavailable: 'The server is currently unavailable. Please try again later.',
  500: 'An unexpected server error occurred. Please try again later.'
};

/**
 * Pick the message to show for a classified error.
 *
 * @param {string|number} reason - Result of `classifyError`
 * @param {Object} overrides - Per-screen wording, keyed by reason, plus a
 *   required `fallback` used for any reason without specific wording
 * @returns {string} - The message to display
 */
export const vmsErrorText = (reason, overrides = {}) =>
  overrides[reason] ?? DEFAULT_MESSAGES[reason] ?? overrides.fallback;

/**
 * Wording for system-level failures reported in `applicationErrors[]`.
 *
 * These describe the submission as a whole - our backend is healthy, but VMS
 * rejected or never answered the request - so none of them name an invoice.
 *
 */
const APPLICATION_ERROR_MESSAGES = {
  PAYLOAD_TOO_LARGE:
    'This batch was too large for the VMS system to accept in a single submission. ' +
    'The invoices that were not processed are still valid - please re-submit them in a smaller file.',
  VMS_URI_TOO_LONG:
    'This batch was too large for the VMS system to accept in a single submission. ' +
    'The invoices that were not processed are still valid - please re-submit them in a smaller file.',
  VMS_UNAUTHORIZED:
    'The VMS system rejected our credentials. Please contact support - there is nothing to correct in your file.',
  VMS_FORBIDDEN:
    'The VMS system rejected our credentials. Please contact support - there is nothing to correct in your file.',
  VMS_ENDPOINT_NOT_FOUND:
    'The VMS system could not be reached at the configured address. Please contact support.',
  VMS_TIMEOUT:
    'The VMS system took too long to respond. The invoices that were not processed can be re-submitted.',
  VMS_RATE_LIMITED:
    'The VMS system is currently limiting how many requests it accepts. ' +
    'Please wait a few minutes, then re-submit the invoices that were not processed.',
  VMS_SERVICE_ERROR:
    'The VMS system reported an error. Please try again later, or contact support if it keeps happening.',
  VMS_BAD_REQUEST: 'The VMS system rejected this submission. Please contact support.',
  VMS_UNSUPPORTED_MEDIA_TYPE:
    'The VMS system rejected the format of this submission. Please contact support.',
  VMS_UNREACHABLE:
    'The VMS system could not be reached. Please try again later, or contact support if it keeps happening.',
  UPLOAD_FAILED:
    'The upload could not be completed. Please try again later, or contact support if it keeps happening.'
};

/**
 * Pull system-level errors out of a failed upload response.
 *
 * @param {Object} error - The rejected axios error
 * @returns {Array|null} - Non-empty applicationErrors, or null
 */
export const getApplicationErrors = (error) => {
  const applicationErrors = error?.response?.data?.applicationErrors;
  return Array.isArray(applicationErrors) && applicationErrors.length > 0 ? applicationErrors : null;
};

/**
 * Pick the message to show for one system-level error. The `code` list is open,
 * so anything unrecognised falls back to the message the API sent.
 *
 * @param {Object} applicationError - One entry of `applicationErrors[]`
 * @returns {string} - The message to display
 */
export const applicationErrorText = (applicationError) =>
  APPLICATION_ERROR_MESSAGES[applicationError?.code] ??
  applicationError?.message ??
  'The VMS system rejected this submission. Please try again later or contact support.';

/**
 * True when the batch failed because it was too big for VMS to take in one go 
 *
 * @param {Array} applicationErrors - Result of `getApplicationErrors`
 * @returns {boolean}
 */
export const isBatchTooLarge = (applicationErrors = []) =>
  applicationErrors.some((e) => e?.code === 'PAYLOAD_TOO_LARGE' || e?.code === 'VMS_URI_TOO_LONG');
